<template>
    <div>
        <rkm-row>
            <rkm-col>
                <rkm-data-table
                    :fields="fields"
                    :repository="repositories.paciente"
                    :disabled-button-search="true"
                    :tbody-tr-class="rowClass"
                    class="tabela-resultado"
                    ref="DataTable"
                    sort-by="nome"
                >
                    <template v-slot:cell(nome)="{ item }">
                        {{ item.nome }}

                        <template v-if="item.nomeSocial">
                            <template v-if="item.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                                <strong>({{ item.nomeSocial }})</strong>
                            </template>

                            <template v-else>
                                ({{ item.nomeSocial }})
                            </template>
                        </template>

                        <span
                            v-if="item.obitoEm"
                            class="ml-1"
                            title="Paciente em óbito"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="cross" />
                        </span>

                        <span
                            v-if="item.bloqueado"
                            class="ml-1"
                            title="Cadastro bloqueado"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="ban" />
                        </span>

                        <span
                            v-if="isProvisorioOuTemporario(item)"
                            class="ml-1"
                            title="Paciente provisório/temporário"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="hourglass-half" />
                        </span>

                        <span
                            v-if="item.classificacaoId == enums.classificacao.OUTRO_MUNICIPIO"
                            class="ml-1"
                            title="Paciente não munícipe"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="map-pin" />
                        </span>

                        <span
                            v-if="!item.cpf && !item.cns"
                            class="ml-1"
                            title="Paciente sem CPF e CNS"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="exclamation-triangle" />
                        </span>

                        <span
                            v-if="item.pacienteResponsavelFamiliar"
                            class="ml-1"
                            title="Responsável familiar"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="crown" />
                        </span>

                        <span
                            v-if="isIdoso(item.dataNascimento)"
                            class="ml-1"
                            title="Idoso"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="blind" />
                        </span>

                        <span
                            v-if="hasTea(item)"
                            class="ml-1"
                            title="Pessoa com Transtorno do Espectro Autista - TEA"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="infinity" />
                        </span>

                        <span
                            v-if="hasPcd(item)"
                            class="ml-1"
                            title="Pessoa com Deficiência - PCD"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="wheelchair" />
                        </span>
                    </template>

                    <template v-slot:cell(dataNascimento)="row">
                        {{ row.value | formatDate }}
                    </template>

                    <template v-slot:cell(cpf)="row">
                        {{ row.value | formatCpf }}
                    </template>

                    <template v-slot:cell(cns)="row">
                        {{ row.value | formatCns }}
                    </template>

                    <template v-slot:cell(actions)="{ item }">
                        <rkm-button
                            @click.stop="search(item)"
                            color="primary"
                            title="Visualizar"
                            small
                        >
                            <rkm-icon icon="search" />
                        </rkm-button>
                    </template>
                </rkm-data-table>
            </rkm-col>
        </rkm-row>

        <rkm-row class="mt-3">
            <rkm-col>
                <p class="m-0">
                    <span class="legend-color purple" /> Paciente em óbito<br>
                    <span class="legend-color danger" /> Cadastro bloqueado<br>
                    <span class="legend-color orange" /> Paciente provisório ou temporário<br>
                    <span class="legend-color brown" /> Paciente não munícipe<br>
                    <span class="legend-color warning" /> Paciente sem CPF e CNS
                </p>
            </rkm-col>
        </rkm-row>

        <ModalAgendamentoExameLaboratorial
            v-if="pacienteId"
            :paciente-id="pacienteId"
            :hasPrint="true"
            :hasAmbulatorio="hasAmbulatorio"
            @save="handleAgendado"
            hide-agendamentos
            ref="ModalAgendamentoExameLaboratorial"
        />
    </div>
</template>

<script>

// Components.
import ModalAgendamentoExameLaboratorial from '@/components/exame-laboratorial/agendamento-exame/Modal'

// Mixins.
import { repository, error, printExameLaboratorial, stringCase } from '@/mixins'

// Filters.
import formats from '@/filters/formats'

// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import impressaoAgendamento from '@/enums/exame-laboratorial/impressaoAgendamento'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

// Others.
import moment from 'moment'

// Vuex.
import {mapActions} from 'vuex'

export default {
    components: {
        ModalAgendamentoExameLaboratorial,
    },
    created() {

        this.repositories = {
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
            parametrizacao: this.getConectaSusRepository('cadastro.parametrizacao'),
        }
    },
    data: () => ({
        form: {},
        pacienteId: null,
        fields: [
            {
                name: 'cadastroCidadao.nome',
                key: 'nome',
                label: 'Nome (Nome social)',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.nomeSocial',
                key: 'nomeSocial',
                label: 'Nome social',
                searchable: true,
                sortable: true,
                visible: false
            },
            {
                name: 'cadastroCidadao.dataNascimento',
                key: 'dataNascimento',
                label: 'Data de nascimento',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.nomeMae',
                key: 'nomeMae',
                label: 'Nome da mãe',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroPaciente.prontuario',
                key: 'prontuario',
                label: 'Prontuário municipal',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.cpf',
                key: 'cpf',
                label: 'CPF',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.cns',
                key: 'cns',
                label: 'CNS',
                searchable: true,
                sortable: true
            },
            {
                name: 'actions',
                key: 'actions',
                label: 'Ações',
                class: 'col-min'
            },
        ],
        repositories: {
            paciente: null,
        },
        enums: {
            preferenciaNome,
            classificacao,
            impressaoAgendamento,
            subclassificacao,
            deficienciaBase,
            deficiencia,
        }
    }),
    filters: {
        formatDate: formats.formatDate,
        formatCns: formats.formatCns,
        formatCpf: formats.formatCpf,
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        isProvisorioOuTemporario(item) {

            return item.classificacaoId == this.enums.classificacao.PROVISORIO
                || item.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO
        },
        isIdoso(dataNascimento) {

            return !!dataNascimento && moment().diff(moment(dataNascimento), 'years') >= 60
        },
        getDeficienciasBaseIds(item) {

            return item.deficienciasBaseIds
                ? item.deficienciasBaseIds.split(',').map(Number)
                : []
        },
        hasTea(item) {

            return this.getDeficienciasBaseIds(item).includes(this.enums.deficienciaBase.TEA)
                || item.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        hasPcd(item) {

            return this.getDeficienciasBaseIds(item).some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!item.deficienciaId && item.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
        rowClass(item, type) {
            if(item && type === 'row') {
                if (item.obitoEm) {
                    return 'table-purple'
                }
                if (item.bloqueado) {
                    return 'table-danger'
                }
                if (this.isProvisorioOuTemporario(item)) {
                    return 'table-orange'
                }
                if (item.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                    return 'table-brown'
                }
                if (!item.cpf && !item.cns) {
                    return 'table-warning'
                }
            }
        },
        search(item) {

            this.setLoading()

            this.repositories.paciente.validCNS(this.camelObjectKeysToSnakeRecursive({pacienteId: item.id}))
                .then(response => {

                    if (item.subclassificacaoId === this.enums.subclassificacao.TEMPORARIO
                        && response.data.additionals.avisaCnsTemporarioInvalido) {

                            const confirm = async () => {

                                this.$nextTick().then(() => this.redirect(item))

                                return false
                            }

                            const msg = `Paciente com cadastro temporário. Deseja continuar?`

                            this.showConfirmation(msg, confirm)

                            return false
                    }

                    if (item.classificacaoId !== this.enums.classificacao.MUNICIPE
                        && item.classificacaoId !== this.enums.classificacao.PROVISORIO
                        && item.classificacaoId !== this.enums.classificacao.OUTRO_MUNICIPIO) {

                            this.showValidationNotification(`Não é permitido realizar agendamento de exames para a
                                classificação do paciente.`)

                            return false
                    }

                    if (item.classificacaoId == this.enums.classificacao.PROVISORIO) {

                        const confirm = async () => {

                            this.$nextTick().then(() => this.redirect(item))

                            return false
                        }

                        if (item.diasCadastroDesatualizado > 0) {

                            let msg = `Paciente tem classificação provisória e com cadastro desatualizado há
                                <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Apenas os exames parametrizados para
                                pacientes provisórios serão listados. Deseja continuar?`

                            if (item.enderecoDesatualizadoBloqueado) {

                                msg = `Paciente tem classificação provisória e com cadastro desatualizado há
                                    <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Endereço do usuário também deve
                                    ser atualizado. Apenas os exames parametrizados para pacientes provisórios serão listados.
                                    Deseja continuar?`
                            }

                            this.showConfirmation(msg, confirm)
                        } else {

                            const msg = `Paciente tem classificação provisória. Apenas os exames parametrizados para pacientes
                                provisórios serão listados. Deseja continuar?`

                            this.showConfirmation(msg, confirm)
                        }

                        return false
                    }

                    if (item.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {

                        const confirm = async () => {

                            this.$nextTick().then(() => this.redirect(item))

                            return false
                        }

                        if (item.diasCadastroDesatualizado > 0) {

                            let msg = `Paciente é de outro município e com cadastro desatualizado há
                                <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Apenas os exames parametrizados para
                                pacientes de outros municípios serão listados. Deseja continuar?`

                            if (item.enderecoDesatualizadoBloqueado) {

                                msg = `Paciente é de outro município e com cadastro desatualizado há
                                    <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Endereço do usuário também deve
                                    ser atualizado. Apenas os exames parametrizados para pacientes de outros municípios serão
                                    listados. Deseja continuar?`
                            }

                            this.showConfirmation(msg, confirm)
                        } else {

                            const msg = `Paciente é de outro município. Apenas os exames parametrizados para pacientes de
                                outros municípios serão listados. Deseja continuar?`

                            this.showConfirmation(msg, confirm)
                        }

                        return false
                    }

                    if (item.diasCadastroDesatualizado > 0) {

                        const confirm = async () => {

                            this.$nextTick().then(() => this.redirect(item))

                            return false
                        }

                        let msg = `Paciente com cadastro desatualizado há <strong>${item.diasCadastroDesatualizado}</strong>
                            dia(s). Deseja continuar?`

                        if (item.enderecoDesatualizadoBloqueado) {

                            msg = `Paciente com cadastro desatualizado há <strong>${item.diasCadastroDesatualizado}</strong>
                                dia(s). Endereço do usuário também deve ser atualizado. Deseja continuar?`
                        }

                        this.showConfirmation(msg, confirm)

                        return false
                    }

                    this.redirect(item)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleEtiquetaPrint() {

            let etiqueta = {
                pacienteNome: this.item.pacienteNome,
                pacienteNomeSocial: this.item.pacienteNomeSocial,
                agendamentoNumero: this.item.agendamentoNumero,
                unidadeNome: this.item.unidade,
                laboratorioNome: this.item.laboratorio,
                idade: this.item.idade,
                prontuario: this.item.prontuario,
                sistemaNome: this.item.sistemaNome,
                sistemaVersao: this.item.sistemaVersao,
                nomePrefeitura: this.item.nomePrefeitura,
            }

            this.print().etiqueta(etiqueta)
        },
        handlePrint() {

            let agendamento = {
                pacienteNome: this.item.pacienteNome,
                pacienteNomeSocial: this.item.pacienteNomeSocial,
                prontuario: this.item.prontuario,
                cpf: formats.formatCpf(this.item.cpf),
                nomeResponsavel: this.item.nomeResponsavel,
                dataNascimento: this.item.dataNascimento,
                agendamentoNumero: this.item.agendamentoNumero,
                medicoNome: this.item.medicoNome,
                data: this.item.data,
                examesIncluido: this.item.examesIncluido,
                horaExame: this.item.horaExame,
                unidade: this.item.unidade,
                observacao: this.item.observacao,
                endereco: this.item.endereco,
                numeroEndereco: this.item.numeroEndereco,
                bairro: this.item.bairro,
                telefone: this.item.telefone,
                tipo: this.item.tipo,
                cep: this.item.cep,
                localColeta: this.item.localColeta,
                enderecoColeta: this.item.enderecoColeta,
                numeroEnderecoColeta: this.item.numeroEnderecoColeta,
                bairroColeta: this.item.bairroColeta,
                tipoColeta: this.item.tipoColeta,
                cepColeta: this.item.cepColeta,
                telefoneColeta: this.item.telefoneColeta,
                laboratorio: this.item.laboratorio,
                enderecoLaboratorio: this.item.enderecoLaboratorio,
                numeroEnderecoLaboratorio: this.item.numeroEnderecoLaboratorio,
                bairroLaboratorio: this.item.bairroLaboratorio,
                tipoLaboratorio: this.item.tipoLaboratorio,
                cepLaboratorio: this.item.cepLaboratorio,
                telefoneLaboratorio: this.item.telefoneLaboratorio,
                logotipo: this.item.logotipo,
                nomePrefeitura: this.item.nomePrefeitura,
                ramoAtividade: this.item.ramoAtividade,
                mensagemProtocolo: this.item.mensagemProtocolo,
                sistemaNome: this.item.sistemaNome,
                sistemaVersao: this.item.sistemaVersao,
                usuario: this.item.usuario,
                usuarioNomeSocial: this.item.usuarioNomeSocial ? this.item.usuarioNomeSocial : null,
                urgente: this.item.urgente,
                dataEntrega: this.item.dataEntrega,
            }

            this.print().agendamento(agendamento)
        },
        redirect(item) {

            if (item.enderecoDesatualizadoBloqueado) {

                this.showAlert(
                    'O endereço do paciente ainda não foi atualizado pela Base Nacional. Atualize o cadastro para continuar.',
                    'Atenção'
                )

                return
            }

            if (this.origem === 'coleta') {

                this.pacienteId = item.id

                this.$nextTick().then(() => this.$refs.ModalAgendamentoExameLaboratorial.open())
            } else {

                if (this.isModal) {
                    this.$emit('create', item.id)
                } else {
                    this.$router.push({ name: 'exame-laboratorial-agendamento-exame-create', params: { id: item.id } })
                }
            }
        },
        handleAgendado() {
            this.$emit('exit')
        },
    },
    mounted () {
        this.$nextTick(function () {

            if (this.item && this.item.agendamentoNumero) {

                this.showSaveNotification()

                if (this.item.impressaoAgendamentoTipoId == this.enums.impressaoAgendamento.PROTOCOLO) {
                    this.handlePrint()
                } else if (this.item.impressaoAgendamentoTipoId == this.enums.impressaoAgendamento.ETIQUETA) {
                    this.handleEtiquetaPrint()
                } else {
                    this.handlePrint()
                    this.handleEtiquetaPrint()
                }
            }
        })
    },
    mixins: [ repository, error, printExameLaboratorial, stringCase ],
    props: {
        item: { type: Object, default: null },
        origem: { type: String, default: null },
        isModal: { type: Boolean },
        hasAmbulatorio: { type: Boolean, default: false },
    },
}
</script>

<style scoped>
.legend-color {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    margin-right: 4px;
}
.legend-color.purple {
    background-color: #6f42c1;
}
.legend-color.danger {
    background-color: var(--danger);
}
.legend-color.orange {
    background-color: #F28C28;
}
.legend-color.brown {
    background-color: #6F4E37;
}
.legend-color.warning {
    background-color: var(--warning);
}

.tabela-resultado >>> .table-orange {
    border-color: #F28C28;
    background-color: #FFE0B2;
}

.tabela-resultado >>> .table-brown {
    border-color: #6F4E37;
    background-color: #D2B48C;
}

.tabela-resultado >>> .table-purple {
    border-color: #6f42c1;
    background-color: #E1D4F2;
}
</style>